import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { useGetCompetitionCollections } from '@pkg/hooks';

import { CompetitionCollectionStatus, getChainFromIndex } from '@app/client';
import { callGetCollectionSearchResults } from '@app/client/resources/global-search';
import { useFantasyLineupStore } from '@app/store/zustand';

export const COLLECTION_SEARCH_CACHE = 'collection-search';

type UseCollectionSearchProps = {
    query?: string;
    wishlistId?: string;
};

export const useCollectionSearch = ({ query }: UseCollectionSearchProps) => {
    const statusCheck = useGetCompetitionCollections();
    const { lineupItems } = useFantasyLineupStore();
    const cache = [COLLECTION_SEARCH_CACHE, query];
    return useQuery({
        queryKey: cache,
        queryFn: ({ signal }) =>
            callGetCollectionSearchResults({ searchQuery: query || '', settings: { signal } })?.then(async (res) => {
                if (res?.ok && res.value) {
                    const slugs = res.value.map((item) => item.slug);
                    const statuses = await statusCheck(slugs);
                    return res.value.map((collection) => {
                        const status = statuses?.find((item) => item.slug === collection.slug)?.status;
                        const alreadyAdded = lineupItems.some((item) => item.slug === collection.slug);

                        return {
                            ...collection,
                            slug: collection.slug || collection.id,
                            enabled: !status || status === CompetitionCollectionStatus.Normal,
                            alreadyAdded,
                        };
                    });
                }
            }),
        retry: false,
        enabled: !!query,
        select: (res) => {
            return res?.map((collection) => ({ ...collection, chain: getChainFromIndex(collection.chain) }));
        },
    });
};

//TODO tidy up
export const useAdminCollectionSearch = ({ query }: UseCollectionSearchProps) => {
    const cache = [COLLECTION_SEARCH_CACHE, query];
    return useQuery({
        queryKey: cache,
        queryFn: ({ signal }) => callGetCollectionSearchResults({ searchQuery: query || '', settings: { signal } }),
        retry: false,
        placeholderData: keepPreviousData,
        enabled: Boolean(query?.length),
        select: (res) => {
            if (res?.ok && res.value) {
                return res.value;
            }
        },
    });
};
